/**
 * System Configuration API Client
 * 
 * Endpoints:
 * - GET/PUT /api/transactions/config/fees/ - Fee configurations
 * - GET/PUT /api/transactions/config/limits/ - KYC limit configurations
 * - GET/POST /api/clients/config/taux/ - Exchange rate
 */
import { apiClient } from './client';

// ============ Types ============

export interface FeeConfig {
    transaction_type: string;
    transaction_type_display: string;
    percentage: number;
    is_active: boolean;
    description: string | null;
    created_at: string;
    updated_at: string;
}

export interface LimitConfig {
    id: string;
    kyc_status: string;
    kyc_status_display: string;
    max_balance: number | null;
    per_transaction_max: number | null;
    daily_transaction_count: number | null;
    daily_amount: number | null;
    monthly_amount: number | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface ExchangeRate {
    id: string;
    taux: number;
    created_at: string;
}

export interface RestrictionConfig {
    id: string;
    is_active: boolean;
    limit_bs_amount: number;
    limit_sol_amount: number;
    limit_admin_amount: number;
    agent_cutoff_time: string;
    admin_email: string;
    updated_at: string;
}

// ============ API Functions ============

export const configApi = {
    // Fee configurations
    getFees: async (): Promise<FeeConfig[]> => {
        const response = await apiClient.get('/api/transactions/config/fees/');
        return response.data;
    },

    updateFee: async (type: string, data: Partial<FeeConfig>): Promise<FeeConfig> => {
        if (!type) throw new Error("Transaction type is required for update");
        const response = await apiClient.patch(`/api/transactions/config/fees/${type}/`, data);
        return response.data;
    },

    createFee: async (data: { transaction_type: string; percentage: number; is_active?: boolean; description?: string }): Promise<FeeConfig> => {
        const response = await apiClient.post('/api/transactions/config/fees/', data);
        return response.data;
    },

    // KYC Limit configurations
    getLimits: async (): Promise<LimitConfig[]> => {
        const response = await apiClient.get('/api/transactions/config/limits/');
        return response.data;
    },

    updateLimit: async (kycStatus: string, data: Partial<LimitConfig>): Promise<LimitConfig> => {
        const response = await apiClient.patch(`/api/transactions/config/limits/${kycStatus}/`, data);
        return response.data;
    },

    createLimit: async (data: Partial<LimitConfig>): Promise<LimitConfig> => {
        const response = await apiClient.post('/api/transactions/config/limits/', data);
        return response.data;
    },

    // Exchange rate
    getCurrentRate: async (): Promise<ExchangeRate> => {
        const response = await apiClient.get('/api/config/taux/');
        return response.data;
    },

    setRate: async (taux: number): Promise<ExchangeRate> => {
        const response = await apiClient.post('/api/config/taux/', { taux });
        return response.data;
    },

    getRateHistory: async (limit: number = 10): Promise<ExchangeRate[]> => {
        const response = await apiClient.get(`/api/config/taux/history/?limit=${limit}`);
        return response.data;
    },

    // Mobile Version
    getMobileVersion: async (platform: string): Promise<any> => {
        const response = await apiClient.get(`/api/config/taux/mobile-version?platform=${platform}`);
        return response.data;
    },

    setMobileVersion: async (data: any): Promise<any> => {
        const response = await apiClient.post('/api/config/taux/mobile-version', data);
        return response.data;
    },

    // Restriction configurations
    getRestrictions: async (): Promise<RestrictionConfig> => {
        const response = await apiClient.get('/api/transactions/config/restrictions/');
        return response.data;
    },

    updateRestrictions: async (data: Partial<RestrictionConfig>): Promise<RestrictionConfig> => {
        const response = await apiClient.patch('/api/transactions/config/restrictions/', data);
        return response.data;
    }
};
